'use client';

import { jobsearchintro } from '@/static/job_search_intro';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { NEXT_PUBLIC_IMAGE_URL } from '@/common';

interface JobSearchVersion {
  title: string;
  titleColor?: string;
  description: string;
  image: string;
  show: boolean;
}

// Tách phần content thành component riêng để có thể sử dụng key
const JobSearchContent: React.FC<{
  version: JobSearchVersion;
}> = ({ version }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.3,
        rootMargin: '0px 0px -100px 0px',
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []); // Chỉ chạy một lần khi mount

  return (
    <section
      ref={sectionRef}
      id="job-search"
      className="overflow-hidden px-10 py-16"
      style={{
        backgroundImage: `url('${NEXT_PUBLIC_IMAGE_URL}/image/landing-page/bg_job_detail.png')`,
        backgroundSize: 'auto 100%',
        backgroundPosition: 'right top',
        backgroundRepeat: 'no-repeat',
        backgroundColor: '#f2f5a2',
      }}
    >
      <div className="max-w-8xl mx-auto">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
          {/* Left Column - Title and Description */}
          <div
            className={`space-y-6 transition-all duration-1000 ease-out ${
              isVisible
                ? 'translate-x-0 opacity-100'
                : '-translate-x-full opacity-0'
            }`}
          >
            <h2 className="text-3xl font-bold text-gray-800 lg:text-5xl lg:leading-tight">
              {version.title}
              <br />
              <span className="text-[#9447FF]">{version.titleColor}</span>
            </h2>
            <p className="text-sm leading-[1.5] text-gray-600 md:text-base">
              {version.description}
            </p>
          </div>

          {/* Right Column - Image */}
          <div
            className={`flex justify-center transition-all delay-300 duration-1000 ease-out lg:justify-end ${
              isVisible
                ? 'translate-x-0 opacity-100'
                : 'translate-x-full opacity-0'
            }`}
          >
            <div className="relative w-full">
              <Image
                src={version.image}
                alt={version.title}
                width={700}
                height={500}
                className="h-auto w-full object-contain"
                loading="lazy"
                sizes="(min-width: 1024px) 50vw, 100vw"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const JobSearch: React.FC = () => {
  const searchParams = useSearchParams();
  const urlVersion = searchParams?.get('version')?.toUpperCase() || 'A';
  const variant = urlVersion === 'A' || urlVersion === 'B' ? urlVersion : 'A';

  // Lấy version tương ứng từ dữ liệu
  const versionKey = `version${variant}` as 'versionA' | 'versionB';
  const version = jobsearchintro[0][versionKey];

  if (!version) {
    console.error(`Version ${variant} not found in jobsearchintro data`);

    return null;
  }

  // Sử dụng key để force re-render khi variant thay đổi
  return <JobSearchContent key={variant} version={version} />;
};

export default JobSearch;
